import { displayHeight, displayWeight } from '../../lib/units'
import type { UnitPreference } from '../../lib/units'

interface Props {
  character: {
    name: string
    occupation_name: string | null
    species_id: string
    sex: string
    sunsign_id: string | null
    culture_id: string
    social_class: string
    deity: string | null
    piety: number
    height_inches: number | null
    weight_lbs: number | null
    comeliness: number | null
    complexion: string | null
    hair_color: string | null
    eye_color: string | null
    generator_seed: { seed?: number; version?: string } | null
    sexuality: string | null
    medical_traits: Array<{ label?: string; id?: string }> | null
    upbringing_type: string | null
    parent_class: string | null
    parent_occupation: string | null
    hobby_skill: string | null
    birth_settlement: string | null
    current_settlement: string | null
    life_phase: string | null
    sibling_rank: string | null
  }
  currentAge: number
  pref: UnitPreference
  isGM?: boolean
}

function comelinessDesc(cml: number): string {
  if (cml <= 5) return 'Ugly'
  if (cml <= 8) return 'Plain'
  if (cml <= 12) return 'Average'
  if (cml <= 15) return 'Attractive'
  return 'Handsome'
}

export function IdentityPanel({ character, currentAge, pref, isGM }: Props) {
  const pietyDots = (n: number) => {
    const filled = Math.min(n, 5)
    const empty = 5 - filled
    return '\u25CF'.repeat(filled) + '\u25CB'.repeat(empty)
  }

  return (
    <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
      <h2 className="text-xl font-bold text-accent border-b border-parchment-dark pb-2 mb-3">
        {character.name}
      </h2>
      <div className="grid grid-cols-2 gap-1 text-sm">
        <div><span className="font-semibold">Occupation:</span> {character.occupation_name ?? '—'}</div>
        <div><span className="font-semibold">Age:</span> {currentAge}</div>
        <div><span className="font-semibold">Species:</span> {character.species_id}</div>
        <div><span className="font-semibold">Sex:</span> {character.sex}</div>
        <div><span className="font-semibold">Sunsign:</span> {character.sunsign_id ?? '—'}</div>
        <div><span className="font-semibold">Culture:</span> {character.culture_id}</div>
        <div><span className="font-semibold">Social Class:</span> {character.social_class}</div>
        <div><span className="font-semibold">Comeliness:</span> {character.comeliness != null ? `${character.comeliness} (${comelinessDesc(character.comeliness)})` : '—'}</div>
        <div><span className="font-semibold">Deity:</span> {character.deity ?? 'Agnostic'}</div>
        <div><span className="font-semibold">Piety:</span> {pietyDots(character.piety)}</div>
        <div><span className="font-semibold">Height:</span> {character.height_inches ? displayHeight(character.height_inches, pref) : '—'}</div>
        <div><span className="font-semibold">Weight:</span> {character.weight_lbs ? displayWeight(character.weight_lbs, pref) : '—'}</div>
        <div><span className="font-semibold">Complexion:</span> {character.complexion ?? '—'}</div>
        <div><span className="font-semibold">Hair:</span> {character.hair_color ?? '—'}</div>
        <div><span className="font-semibold">Eyes:</span> {character.eye_color ?? '—'}</div>
        {character.life_phase && <div><span className="font-semibold">Phase:</span> {character.life_phase}</div>}
        {character.birth_settlement && <div><span className="font-semibold">Settlement:</span> {character.current_settlement ?? character.birth_settlement}{character.birth_settlement !== character.current_settlement && character.current_settlement ? ` (born: ${character.birth_settlement})` : ''}</div>}
        {character.upbringing_type && <div><span className="font-semibold">Upbringing:</span> {character.upbringing_type}</div>}
        {character.sibling_rank && <div><span className="font-semibold">Birth Order:</span> {character.sibling_rank}</div>}
        {character.parent_class && <div><span className="font-semibold">Parent:</span> {character.parent_occupation ?? character.parent_class}</div>}
        {character.hobby_skill && <div><span className="font-semibold">Hobby:</span> {character.hobby_skill}</div>}
        {isGM && character.sexuality && character.sexuality !== 'heterosexual' && <div><span className="font-semibold">Sexuality:</span> {character.sexuality}</div>}
        {character.medical_traits && Array.isArray(character.medical_traits) && character.medical_traits.length > 0 && (
          <div className="col-span-2 text-danger"><span className="font-semibold">Medical:</span> {character.medical_traits.map((t: { label?: string; id?: string }) => t.label ?? t.id).join(', ')}</div>
        )}
        {isGM && character.generator_seed?.seed && (
          <div className="col-span-2 mt-1 pt-1 border-t border-parchment/50">
            <span className="font-semibold text-ink-light text-xs">Seed:</span>{' '}
            <span className="text-xs text-ink-light font-mono">{character.generator_seed.seed}</span>
            {character.generator_seed.version && (
              <span className="text-xs text-ink-light/50 ml-2">v{character.generator_seed.version}</span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
